import React, { useEffect, useMemo, useState } from 'react';
import { useAppShell } from '../contexts/AppShellContext';
import { getMaterialReceipts } from '../services/materialReceiptService';
import type { MaterialReceiptRow } from '../types/api';
import { PageHeader } from '../components/PageHeader';
import { SearchInput } from '../components/SearchInput';
import { windowCounts, type ScopeValue } from '../utils/timeWindow';

// A record of Regulatory Cell output — every matched Regulatory job that has
// reached the end of the production pipeline (regulatory_stage === 'completed',
// i.e. printed). Distinct from the live Regulatory intake queue on Material
// Intake: this is history, not a work queue.

const th = 'text-left text-xs font-semibold uppercase tracking-wide px-2 py-2 border-b border-trim whitespace-nowrap bg-wash sticky top-0 z-10';
const td = 'px-2 py-2 border-b border-wash text-xs text-copy align-top whitespace-nowrap';

// Method is a constant today — every Regulatory match auto-creates its MIC
// mail bucket with this mailing method (see AcePage.handleConfirmMatch).
const MIC_METHOD = 'Pylon Logistic Standard';
const MIC_JOB_TYPE = 'Interim Mailing - Essential';
const MIC_ENVELOPE_TYPES = ['6x9 Dual Window', '#10 Window', '9x12 Window'];

function hashSeed(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
}

// Bucket #, barcode, piece counts, and envelope type aren't persisted by the
// MIC placeholder integration yet (see micService.ts) — mocked here,
// deterministically per receipt so values stay stable across this page's 3s
// poll instead of re-randomizing on every refresh.
function mockProductionFields(r: MaterialReceiptRow) {
  const seed = hashSeed(r.id);
  const bucketNo = String(770000 + (seed % 10000));
  const qty = 1 + (seed % 50);
  const envelope = r.mail_envelope_size || MIC_ENVELOPE_TYPES[seed % MIC_ENVELOPE_TYPES.length];
  const jobNo = r.job_numbers[0] || '—';
  const domesticForeign = seed % 5 === 0 ? 'Foreign' : 'Domestic';
  const barcode = `${bucketNo}|${jobNo}|${qty}|${domesticForeign}|Interim`;
  return { bucketNo, qty, envelope, barcode };
}

export function RegulatoryProductionLogPage() {
  const { setTopbar } = useAppShell();
  const [receipts, setReceipts] = useState<MaterialReceiptRow[]>([]);
  // Period is a monitoring lens on the stats band only, same as the
  // Regulatory tab on Material Intake — it does not hide rows here either,
  // since a job's intake date isn't when it was completed.
  const [scope, setScope] = useState<ScopeValue>({ window: 'today' });
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    const load = () => getMaterialReceipts().then(r => { if (alive) setReceipts(r); }).catch(() => {});
    load();
    const iv = setInterval(load, 3000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const rows = useMemo(
    () => receipts
      .filter(r => r.job_category === 'R' && r.regulatory_stage === 'completed')
      .sort((a, b) => (b.intake_date ?? '').localeCompare(a.intake_date ?? '')),
    [receipts],
  );

  const winCounts = useMemo(() => windowCounts(rows), [rows]);

  const filteredRows = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return rows;
    return rows.filter(r => (
      `${r.client_names.join(' ')} ${r.job_numbers.join(' ')} ${r.scan_number} ${r.book_name ?? ''}`.toLowerCase().includes(q)
    ));
  }, [rows, search]);

  useEffect(() => { setTopbar('Production Log', `${rows.length} completed jobs`); }, [setTopbar, rows.length]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader scope={scope} onScopeChange={setScope} counts={winCounts} />

      <div className="flex items-center gap-3 px-6 pt-4 pb-4 flex-shrink-0">
        <div className="px-2.5 py-0.5 rounded-full text-xs font-semibold" style={{ background: '#F5F7FA', color: '#595A60' }}>{filteredRows.length} jobs</div>
        <div className="ml-auto">
          <SearchInput value={search} onChange={setSearch} placeholder="Search scans, clients…" width={220} />
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 pb-6">
        <table className="text-xs" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
          <thead>
            <tr>
              <th className={th}>Barcode</th>
              <th className={th}>Job #</th>
              <th className={th}>!QTY</th>
              <th className={th}>QTY</th>
              <th className={th}>Job Type</th>
              <th className={th}>Method</th>
              <th className={th}>Envelope Type</th>
              <th className={th}>Job Name</th>
              <th className={th}>Notes</th>
              <th className={th}>Complete</th>
              <th className={th}>Date</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map(r => {
              const mock = mockProductionFields(r);
              const lastNote = r.note_log.length ? r.note_log[r.note_log.length - 1].text : '';
              return (
                <tr key={r.id} className="hover:bg-wash transition-colors">
                  <td className={`${td} text-muted`}>{mock.barcode}</td>
                  <td className={`${td} font-medium`} style={{ color: r.job_numbers.length ? '#2563EB' : undefined }}>{r.job_numbers.join(', ') || '—'}</td>
                  <td className={td}>{mock.qty}</td>
                  <td className={td}>{mock.qty}</td>
                  <td className={td}>{MIC_JOB_TYPE}</td>
                  <td className={td}>{MIC_METHOD}</td>
                  <td className={td}>{mock.envelope}</td>
                  <td className={`${td} font-medium text-ink max-w-[220px] truncate`} title={r.client_names.join(', ')}>{r.client_names.join(', ') || '—'}</td>
                  <td className={`${td} max-w-[200px] truncate`} title={lastNote}>{lastNote || '—'}</td>
                  <td className={td}>
                    <span className="inline-flex items-center gap-1 text-ink">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0F6E56" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      Printed
                    </span>
                  </td>
                  <td className={td}>{r.intake_date ?? '—'}</td>
                </tr>
              );
            })}
            {filteredRows.length === 0 && (
              <tr><td colSpan={11} className="text-center py-12 text-muted text-sm">{rows.length === 0 ? 'No completed jobs yet.' : 'No jobs match the current search.'}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
